import React, { useState, useEffect } from 'react';
import Modal from '../UI/Modal/Modal';
import ConfirmationModal from '../UI/ConfirmationModal/ConfirmationModal';
import { authService } from '../../apiServices/authService';
import { ChangePasswordRequest } from '../../models/auth';
import { ValidationConstants, ValidationUtils } from '../../utils';
import { useNotification } from '../../contexts/NotificationContext';
import './ChangePasswordModal.css';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose }) => {
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const { showNotification } = useNotification();

  useEffect(() => {
    if (isOpen) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setErrors({});
      setShowConfirmClose(false);
    }
  }, [isOpen]);

  const hasFormChanges = (): boolean => {
    return currentPassword !== '' || newPassword !== '' || confirmPassword !== '';
  };

  const handleCloseAttempt = () => {
    if (hasFormChanges()) {
      setShowConfirmClose(true);
    } else {
      handleClose();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmClose(false);
    handleClose();
  };

  const handleCancelClose = () => {
    setShowConfirmClose(false);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!currentPassword) {
      newErrors.currentPassword = 'Поточний пароль обов\'язковий';
    } else if (currentPassword.length > ValidationConstants.User.PasswordMaxLength) {
      newErrors.currentPassword = `Пароль не може перевищувати ${ValidationConstants.User.PasswordMaxLength} символів`;
    }

    if (!newPassword) {
      newErrors.newPassword = 'Новий пароль обов\'язковий';
    } else if (newPassword.length < ValidationConstants.User.PasswordMinLength) {
      newErrors.newPassword = `Пароль повинен містити мінімум ${ValidationConstants.User.PasswordMinLength} символів`;
    } else if (newPassword.length > ValidationConstants.User.PasswordMaxLength) {
      newErrors.newPassword = `Пароль не може перевищувати ${ValidationConstants.User.PasswordMaxLength} символів`;
    } else if (!ValidationUtils.hasDigit(newPassword)) {
      newErrors.newPassword = 'Пароль повинен містити принаймні одну цифру';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Підтвердження пароля обов\'язкове';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Паролі не співпадають';
    }

    if (currentPassword && newPassword && currentPassword === newPassword) {
      newErrors.newPassword = 'Новий пароль повинен відрізнятися від поточного';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const request: ChangePasswordRequest = {
        currentPassword,
        newPassword,
        confirmNewPassword: confirmPassword,
      };

      await authService.changePassword(request);
      showNotification('Пароль успішно змінено', 'success');
      handleClose();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Помилка зміни пароля';
      if (errorMessage.includes('Current password')) {
        setErrors({ currentPassword: 'Поточний пароль невірний' });
      } else {
        setErrors({ general: errorMessage });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setErrors({});
    onClose();
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      title="Зміна пароля"
      onClose={handleCloseAttempt}
      size="sm"
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCloseAttempt}
            disabled={isLoading}
          >
            Скасувати
          </button>
          <button
            type="submit"
            form="change-password-form"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? 'Збереження...' : 'Змінити пароль'}
          </button>
        </>
      }
    >
      <form id="change-password-form" onSubmit={handleSubmit} className="change-password-form">
        <div className="form-group">
          <label htmlFor="current-password" className="form-label">
            Поточний пароль
          </label>
          <input
            type="password"
            id="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className={errors.currentPassword ? 'form-input error' : 'form-input'}
            placeholder="Введіть поточний пароль"
            required
            maxLength={ValidationConstants.User.PasswordMaxLength}
            disabled={isLoading}
          />
          {errors.currentPassword && (
            <span className="error-message">{errors.currentPassword}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="new-password" className="form-label">
            Новий пароль
          </label>
          <input
            type="password"
            id="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className={errors.newPassword ? 'form-input error' : 'form-input'}
            placeholder="Введіть новий пароль"
            required
            minLength={ValidationConstants.User.PasswordMinLength}
            maxLength={ValidationConstants.User.PasswordMaxLength}
            disabled={isLoading}
          />
          {errors.newPassword && (
            <span className="error-message">{errors.newPassword}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="confirm-password" className="form-label">
            Підтвердження пароля
          </label>
          <input
            type="password"
            id="confirm-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={errors.confirmPassword ? 'form-input error' : 'form-input'}
            placeholder="Підтвердіть новий пароль"
            required
            maxLength={ValidationConstants.User.PasswordMaxLength}
            disabled={isLoading}
          />
          {errors.confirmPassword && (
            <span className="error-message">{errors.confirmPassword}</span>
          )}
        </div>

        {errors.general && (
          <div className="error-message general-error">
            {errors.general}
          </div>
        )}
      </form>
    </Modal>

    <ConfirmationModal
      isOpen={showConfirmClose}
      title="Скасувати зміну пароля?"
      message="У вас є незбережені зміни. Ви впевнені, що хочете закрити форму без збереження?"
      confirmText="Так"
      cancelText="Ні"
      onConfirm={handleConfirmClose}
      onCancel={handleCancelClose}
      variant="warning"
    />
    </>
  );
};

export default ChangePasswordModal;

